import { payloadMediaKind } from 'components/answers/Answer/payloadMediaKind'
import {
    answerImageInputClassificationItem,
    answerRecordingInputClassificationItem,
    aiInputDescriptorsForAnswer,
    aiRunOutputIsConsumedInput,
    fileInputClassificationItem,
    mediaEntryIsAiInput,
} from 'lib/aiInputMediaClassification'
import { firstValidMediaTimestamp, sortMediaByCreation } from 'lib/mediaDisplayOrder'

const AUDIO_FILE_EXTENSION_RE =
    /\.(mp3|wav|ogg|oga|opus|m4a|aac|flac|aif|aiff|aifc|caf|amr)(?:[?#]|$)/i
const MP4_FILE_EXTENSION_RE = /\.(mp4|m4v)(?:[?#]|$)/i

const normalizePlayMediaUrl = (rawUrl, apiUrl = null) => {
    if (typeof rawUrl !== 'string' || !rawUrl) return rawUrl
    if (
        rawUrl.startsWith('//') ||
        rawUrl.startsWith('http://') ||
        rawUrl.startsWith('https://') ||
        rawUrl.startsWith('blob:') ||
        rawUrl.startsWith('data:')
    ) {
        return rawUrl
    }

    if (rawUrl.startsWith('/api/') && apiUrl) {
        return `${String(apiUrl).replace(/\/+$/, '')}${rawUrl}`
    }

    return rawUrl
}

const mediaIdentityKeys = item => {
    const attachmentId = item?.attachment_id || item?.attachmentId || item?.id
    const blobId = item?.blob_id || item?.blobId

    return [
        attachmentId ? `attachment:${String(attachmentId)}` : null,
        blobId ? `blob:${String(blobId)}` : null,
    ].filter(Boolean)
}

const mediaUrls = item => {
    const urls = item?.urls || {}
    const previewUrls = item?.preview_urls || item?.previewUrls || {}

    return [
        item?.url,
        item?.file_url,
        item?.image_url,
        item?.preview_url,
        item?.previewUrl,
        item?.preview_image_url,
        item?.previewImageUrl,
        item?.thumbnail_url,
        item?.thumbnailUrl,
        urls.original,
        urls.large,
        urls.medium,
        urls.small,
        previewUrls.original,
        previewUrls.large,
        previewUrls.medium,
        previewUrls.small,
        previewUrls['1x1'],
    ].filter(Boolean)
}

const primaryMediaUrl = item =>
    item?.url ||
    item?.file_url ||
    item?.image_url ||
    item?.urls?.original ||
    item?.urls?.large ||
    item?.urls?.medium ||
    item?.urls?.small ||
    item?.preview_url ||
    item?.previewUrl ||
    null

const imageMediaUrl = item =>
    item?.preview_url ||
    item?.previewUrl ||
    item?.urls?.medium ||
    item?.urls?.small ||
    item?.urls?.large ||
    item?.urls?.original ||
    item?.preview_urls?.medium ||
    item?.preview_urls?.small ||
    item?.preview_urls?.large ||
    item?.preview_urls?.original ||
    item?.image_url ||
    item?.url ||
    null

const videoPosterUrl = item => {
    const previewUrls = item?.preview_urls || item?.previewUrls || {}

    return (
        item?.preview_image_url ||
        item?.previewImageUrl ||
        item?.preview_url ||
        item?.previewUrl ||
        item?.thumbnail_url ||
        item?.thumbnailUrl ||
        previewUrls['1x1'] ||
        previewUrls.medium ||
        previewUrls.small ||
        null
    )
}

const descriptorContentType = item => item?.mime || item?.content_type || item?.contentType || null

const fileFromDescriptor = (item, url, kind) => ({
    ...item,
    id: item?.id || item?.attachment_id || item?.attachmentId,
    url,
    name:
        item?.name ||
        item?.filename ||
        item?.original_filename ||
        (kind === 'audio' ? 'Audio' : 'File'),
    content_type: descriptorContentType(item) || (kind === 'audio' ? 'audio/mpeg' : undefined),
})

const playMediaFromDescriptor = (item, apiUrl) => {
    const kind = payloadMediaKind(item)
    const url = normalizePlayMediaUrl(primaryMediaUrl(item), apiUrl)
    if (!url) return null

    if (kind === 'image') {
        return {
            kind: 'image',
            src: normalizePlayMediaUrl(imageMediaUrl(item), apiUrl),
        }
    }

    if (kind === 'video') {
        const contentType = descriptorContentType(item)
        const isMp4 = contentType === 'video/mp4' || MP4_FILE_EXTENSION_RE.test(url)
        return {
            kind: 'video',
            src: isMp4 ? null : url,
            mp4Src: isMp4 ? url : null,
            srcType: isMp4 ? null : contentType,
            poster: normalizePlayMediaUrl(videoPosterUrl(item), apiUrl),
        }
    }

    if (kind === 'audio') {
        return {
            kind: 'audio',
            file: fileFromDescriptor(item, url, kind),
        }
    }

    return {
        kind: 'file',
        file: fileFromDescriptor(item, url, kind),
    }
}

const recordingKind = answer => {
    const declaredType = String(answer?.recording_type || answer?.original_recording_type || '')
        .split(';')[0]
        .trim()
        .toLowerCase()
    const url = answer?.recording_url || answer?.mp4_recording_url || ''

    if (declaredType.startsWith('audio/') || AUDIO_FILE_EXTENSION_RE.test(url)) return 'audio'
    return 'video'
}

const recordingMedia = (answer, apiUrl) => {
    const rawRecordingUrl = answer?.recording_url || null
    const rawMp4Url = answer?.mp4_recording_url || null
    const recordingUrl = normalizePlayMediaUrl(rawRecordingUrl, apiUrl)
    const mp4Url = normalizePlayMediaUrl(rawMp4Url, apiUrl)
    if (!recordingUrl && !mp4Url) return null

    if (recordingKind(answer) === 'audio') {
        const url = recordingUrl || mp4Url
        return {
            kind: 'audio',
            file: {
                id: answer?.recording_attachment_id || answer?.mp4_recording_attachment_id || null,
                url,
                name: answer?.recording_filename || 'Audio',
                content_type:
                    answer?.recording_type || answer?.original_recording_type || 'audio/mpeg',
            },
        }
    }

    const recordingIsMp4 = MP4_FILE_EXTENSION_RE.test(recordingUrl || '')
    const fallbackSrc =
        recordingUrl && !recordingIsMp4 && recordingUrl !== mp4Url ? recordingUrl : null
    const declaredType = answer?.recording_type || answer?.original_recording_type || ''
    return {
        kind: 'video',
        src: fallbackSrc,
        mp4Src: mp4Url || (recordingIsMp4 ? recordingUrl : null),
        srcType: fallbackSrc ? (declaredType.includes('/') ? declaredType : 'video/webm') : null,
        poster: normalizePlayMediaUrl(answer?.recording_thumbnail_url, apiUrl),
    }
}

export const getAnswerPlayMediaItems = (answer, options = {}) => {
    const apiUrl =
        options.apiUrl ?? (typeof window !== 'undefined' ? window.knovApiUrl || null : null)
    const aiInputDescriptors = aiInputDescriptorsForAnswer(answer)
    const entries = []
    const seenSources = []

    const addEntry = (media, source, createdAt = null) => {
        if (!media) return

        const identityKeys = mediaIdentityKeys(source)
        const urls = mediaUrls(source)
        const isDuplicate = seenSources.some(previousSource => {
            const sharesIdentity = identityKeys.some(key =>
                previousSource.identityKeys.includes(key),
            )
            if (sharesIdentity) return true

            const eitherSourceLacksIdentity =
                !identityKeys.length || !previousSource.identityKeys.length
            return eitherSourceLacksIdentity && urls.some(url => previousSource.urls.includes(url))
        })
        if (isDuplicate) return

        seenSources.push({ identityKeys, urls })
        entries.push({
            media,
            created_at: createdAt,
            attached_at: source?.attached_at || source?.attachedAt || null,
            blob_created_at: source?.blob_created_at || source?.blobCreatedAt || null,
            sequence: entries.length,
        })
    }

    const aiRuns = Array.isArray(answer?.ai_runs) ? answer.ai_runs : []
    aiRuns.forEach(run => {
        const outputs = Array.isArray(run?.output_urls) ? run.output_urls : []
        outputs.forEach(output => {
            if (aiRunOutputIsConsumedInput(run, output, aiInputDescriptors)) return
            addEntry(
                playMediaFromDescriptor(output, apiUrl),
                output,
                firstValidMediaTimestamp(
                    output?.created_at,
                    output?.createdAt,
                    run?.created_at,
                    run?.createdAt,
                ),
            )
        })
    })

    const imagesV2 = (Array.isArray(answer?.images_v2) ? answer.images_v2 : []).filter(image =>
        Boolean(imageMediaUrl(image)),
    )
    const legacyImages = Array.isArray(answer?.image_urls) ? answer.image_urls : []
    const imageEntries = imagesV2.length
        ? sortMediaByCreation(imagesV2)
        : sortMediaByCreation(legacyImages)
    imageEntries.forEach(image => {
        if (mediaEntryIsAiInput(image, aiInputDescriptors)) return
        const src = normalizePlayMediaUrl(imageMediaUrl(image), apiUrl)
        if (!src) return
        addEntry(
            { kind: 'image', src },
            image,
            firstValidMediaTimestamp(image?.created_at, image?.createdAt),
        )
    })

    if (!imageEntries.length && answer?.answer_image_url) {
        const fallbackImage = answerImageInputClassificationItem(answer)
        if (!mediaEntryIsAiInput(fallbackImage, aiInputDescriptors)) {
            const src = normalizePlayMediaUrl(answer.answer_image_url, apiUrl)
            addEntry(
                { kind: 'image', src },
                fallbackImage,
                answer?.updated_at || answer?.created_at,
            )
        }
    }

    const recordingItem = answerRecordingInputClassificationItem(answer)
    if (recordingItem.url && !mediaEntryIsAiInput(recordingItem, aiInputDescriptors)) {
        addEntry(
            recordingMedia(answer, apiUrl),
            recordingItem,
            answer?.recording_created_at || answer?.updated_at,
        )
    }

    const files = Array.isArray(answer?.file_urls) ? answer.file_urls : []
    files.forEach(file => {
        const fileItem = fileInputClassificationItem(file)
        if (mediaEntryIsAiInput(fileItem, aiInputDescriptors)) return
        addEntry(
            playMediaFromDescriptor(file, apiUrl),
            fileItem,
            file?.created_at || file?.createdAt || answer?.updated_at,
        )
    })

    const mediaItems = sortMediaByCreation(entries).map(entry => entry.media)

    if (!mediaItems.length && options.primaryLink) {
        mediaItems.push({ kind: 'link', url: options.primaryLink })
    }

    return mediaItems.length ? mediaItems : [{ kind: 'text' }]
}
